/**
 * The Destination service manages the sequence of pages a user may or may not have to go through during a
 * particular sign in process (for example, some users need to pick an organization and others do not).
 *
 * In addition, it manages things like remembering where to send the user once the process is completed.
 */
import { IApiService } from '../api/api.service';
import { Features } from '../../../../../../../../frontend/libs/shared/src/lib/types/features';
import { UserAccount } from '../api/users.service';

export default class DestinationService {
  termsOfUsePath = 'termsOfUse';
  chooseOrganizationPath = 'chooseOrganization';

  // Set the final destination to be the Home page by default
  defaultDestination = '/aptitude/static/platform/home/landing';
  finalDestination = '/aptitude/static/platform/home/landing';

  mustChooseOrganization = false;
  mustAcceptTermsOfUse = false;

  organizations;

  constructor(
    private $location,
    private $window,
    private api: IApiService,
    private $q,
  ) {
    'ngInject';
  }

  /**
   * If we were passed in a different final destination we can use that to override the default destination
   * (which is the Home page for everybody)
   */
  setFinalDestination(newDestination) {
    this.finalDestination = newDestination;
  }

  /**
   * Functions which help us know intermediate destinations the user may have to visit before we send him/her on to
   * the final destination.
   *
   * There are several different pages which might all be entry points for a user (for example, Sign In, Create a New
   * Password, and Choose an Organization. Thus repeating the logic of getting the list of organizations on each one,
   * possibly advancing to a page to choose one, and setting one shouldn't be repeated multiple times. Instead it has
   * been moved to here.
   *
   * Make sure to pay attention to the promise returned from this. Don't make a call to nextDestination until after
   * this promise has resolved.
   */
  callAfterAuthenticated() {
    return this.api.users.organizations('me').then((results) => {
      this.organizations = results;

      if (results.length > 1) {
        // If the user has more than one organization, note that the user must choose an organization.
        this.mustChooseOrganization = true;

        return results;
      } else {
        // If the user has only one organization, select it immediately.
        return this.chooseOrganization(results[0].organization.id);
      }
    });
  }

  /**
   * Return either a promise of the list of organizations or the cached set to the user.
   */
  getOrganizations() {
    return this.callAfterAuthenticated();
  }

  /**
   * Choose the organization for the user.
   *
   * @param {number} orgId
   */
  chooseOrganization(orgId: number) {
    return this.api.users.switchAccount('me', orgId).then(() => {
      // Note that the user no longer has to choose an organization.
      this.mustChooseOrganization = false;

      return true;
    });
  }

  needsTermsOfUse() {
    this.mustAcceptTermsOfUse = true;
  }

  /**
   * Functions which clear conditions so the user can progress through the workflow of screens.
   */
  termsOfUseAccepted() {
    this.mustAcceptTermsOfUse = false;
  }

  /**
   * Note: Get destination may return a series of intermediate destinations before getting to the final one.
   * For example, a user may need to see the Terms of Use page or the Choose an Organization page first.
   */
  nextDestination(features: Features, account: UserAccount) {
    if (this.mustAcceptTermsOfUse) {
      this.$location.path(this.termsOfUsePath);
    } else if (this.mustChooseOrganization) {
      this.$location.path(this.chooseOrganizationPath);
    } else {
      // We may have to add logic here to filter destinations to which the user does not have access.

      this.$q
        .all([this.api.features.getFeatures(), this.api.users.currentUser()])
        .then(([features, account]) => {
          if (account.authenticationSource === 'CORPORATE_SSO') {
            this.finalDestination = '/aptitude/static/platform/home/categories';
          }

          this.$window.location = this.finalDestination;
        });
    }
  }

  logout() {
    this.$window.location.href = '/aptitude/resources/aptitude_logout';
  }
}
